<template>
  <div class="icon-selection-component">
    <label :for="`icon-input-${label}`">{{ label }}</label>
    <!-- Input qui ressemble à un champ de saisie -->
    <button
      :id="`icon-input-${label}`"
      type="button"
      data-cy="icon-select-trigger"
      class="icon-input w-100 d-flex align-items-center justify-content-between"
      :class="{ focused: isModalOpen }"
      :aria-label="
        selectedIcon
          ? `Icône sélectionnée: ${selectedIcon}. Cliquer pour modifier`
          : 'Choisir une icône'
      "
      :aria-expanded="isModalOpen"
      aria-haspopup="dialog"
      @click="openModal"
    >
      <div class="selected-icon-display">
        <div v-if="selectedIcon">
          <font-awesome-icon
            :icon="['fas', selectedIcon]"
            class="selected-icon"
            aria-hidden="true"
          />
        </div>
        <span v-else class="placeholder-input">Choisir une icône</span>
      </div>
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="dropdown-arrow"
        aria-hidden="true"
      />
    </button>

    <!-- Modal de sélection d'icônes -->
    <div v-if="isModalOpen" class="modal-overlay" role="presentation" @click="closeModal">
      <div
        class="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="icon-modal-title"
        @click.stop
      >
        <div class="modal-header">
          <h3 id="icon-modal-title">Sélectionner une icône</h3>
          <button
            type="button"
            @click="closeModal"
            class="close-btn"
            aria-label="Fermer la fenêtre de sélection"
          >
            <font-awesome-icon :icon="['fas', 'times']" aria-hidden="true" />
          </button>
        </div>

        <div class="modal-body">
          <!-- Barre de recherche -->
          <div class="search-container">
            <label for="icon-search-input" class="visually-hidden">Rechercher une icône</label>
            <input
              id="icon-search-input"
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher une icône..."
              class="search-input"
              data-cy="icon-search"
              ref="searchInput"
              aria-label="Rechercher une icône"
            />
          </div>

          <!-- Grille d'icônes -->
          <div class="icons-grid" role="grid" aria-label="Grille de sélection d'icônes">
            <button
              v-for="(iconObj, iconName) in filteredIcons"
              :key="iconName"
              type="button"
              class="icon-item"
              data-cy="icon-item"
              :data-cy-name="iconName"
              :class="{ selected: selectedIcon === iconName }"
              :aria-label="`Icône ${iconName}`"
              :aria-pressed="selectedIcon === iconName"
              @click="selectIcon(iconName)"
            >
              <font-awesome-icon :icon="['fas', iconName]" class="icon" aria-hidden="true" />
            </button>
          </div>

          <div
            v-if="Object.keys(filteredIcons).length === 0"
            class="no-results"
            role="status"
            aria-live="polite"
          >
            Aucune icône trouvée
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IconSelectionComponent',
  props: {
    modelValue: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Choisir une icône'
    },
    label: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isModalOpen: false,
      selectedIcon: this.modelValue,
      searchQuery: '',
      tempSelectedIcon: null
    }
  },
  computed: {
    iconMap() {
      return this.$iconMap || {}
    },
    filteredIcons() {
      if (!this.searchQuery) {
        return this.iconMap
      }

      const query = this.$normalizeString(this.searchQuery, true)
      const filtered = {}

      Object.keys(this.iconMap).forEach((iconName) => {
        const iconData = this.iconMap[iconName]

        // Recherche dans le nom de l'icône
        const nameMatch = this.$normalizeString(iconName, true).includes(query)

        // Recherche dans les termes français
        const frMatch = iconData.fr?.some((term) =>
          this.$normalizeString(term, true).includes(query)
        )

        // Recherche dans les termes anglais
        const enMatch = iconData.en?.some((term) =>
          this.$normalizeString(term, true).includes(query)
        )

        if (nameMatch || frMatch || enMatch) {
          filtered[iconName] = iconData
        }
      })

      return filtered
    }
  },
  watch: {
    modelValue(newValue) {
      this.selectedIcon = newValue
    }
  },
  methods: {
    openModal() {
      this.isModalOpen = true
      this.tempSelectedIcon = this.selectedIcon
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    closeModal() {
      this.isModalOpen = false
      this.searchQuery = ''
    },
    selectIcon(iconName) {
      this.tempSelectedIcon = iconName
      this.selectedIcon = iconName
      this.$emit('update:modelValue', this.selectedIcon)
      this.closeModal()
    }
  }
}
</script>

<style scoped>
.icon-selection-component {
  position: relative;
  width: 100%;
}

/* Glass label */
label {
  font-weight: 500;
  margin-bottom: 8px;
  font-size: var(--font-size-sm); /* 16px - Standard web */
  color: #ffffff !important;
  display: block;
}

/* Glass input style */
.icon-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  padding: 12px 16px !important;
  cursor: pointer;
  transition: all 0.3s ease !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 46px;
  width: 100%;
}

.icon-input:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: #6bb4f8 !important;
}

.icon-input.focused {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: #6bb4f8 !important;
  box-shadow:
    0 0 0 3px rgba(107, 180, 248, 0.1),
    inset 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.selected-icon-display {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.selected-icon {
  font-size: var(--font-size-md); /* 18px */
  color: #ffffff !important;
}

.placeholder-input {
  background-color: transparent !important;
  color: var(--c-white);
  opacity: 0.4 !important;
  font-style: italic;
  font-size: var(--font-size-sm); /* 16px */
}

.dropdown-arrow {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: var(--font-size-xs); /* 14px */
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  width: 80%;
  max-width: 500px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h3 {
  margin: 0;
  font-size: var(--font-size-lg); /* 20px */
  color: #ffffff !important;
  font-weight: 500;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: var(--font-size-md); /* 18px */
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  transform: scale(1.05);
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.search-container {
  padding: 16px 20px !important;
}

.search-input {
  width: 100%;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 10px !important;
  font-size: 14px;
  color: #ffffff !important;
  transition: all 0.3s ease !important;
}

.search-input:focus {
  outline: none !important;
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: #6bb4f8 !important;
  box-shadow: 0 0 0 3px rgba(107, 180, 248, 0.2) !important;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.icons-grid {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-right: 5px;
  padding-top: 0px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 6px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.icon-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #6bb4f8;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.icon-item.selected {
  background: rgba(107, 180, 248, 0.2);
  border-color: #6bb4f8;
  box-shadow: 0 0 0 2px rgba(107, 180, 248, 0.3);
}

.icon-item .icon {
  font-size: var(--font-size-lg); /* 20px */
  color: rgba(255, 255, 255, 0.8);
}

.icon-item.selected .icon {
  color: #6bb4f8;
}

.no-results {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  padding: 30px 20px;
  font-style: normal;
}

@media (max-width: 576px) {
  .search-input {
    font-size: var(--font-size-sm); /* 16px — était 14px, provoquait un zoom auto iOS au focus */
  }

  .close-btn {
    width: 44px;
    height: 44px; /* cible tactile ≥ 44px, était 32px */
  }

  .modal-content {
    max-height: min(85vh, 500px);
  }
}
</style>
